/* eslint-disable react/no-danger */
import Head from 'next/head';
import Script from 'next/script';
import InnerHTML from 'dangerously-set-html-content';
import { generateMetaTags, generateStructuredData } from '@/utils/getPageMetadata';

type PageHeader = {
  slug: string;
  title: string;
  description: string;
  tourImage: string;
  website: string;
  fbPixel: string;
  children: any;
  htmlCode: string;
  faviconUrl: string;
  type: string;
  fbAppId: string;
  charset: string;
};

const PageHeader = ({
  slug,
  title,
  description = '',
  tourImage,
  website,
  fbPixel,
  children,
  htmlCode,
  faviconUrl,
  type,
  fbAppId,
  charset,
}: PageHeader) => {
  const finalFbAppId = fbAppId && fbAppId.length > 0 ? fbAppId : '741048509750318';

  const metaTags = generateMetaTags({
    title,
    description,
    tourImage,
    website,
    slug,
    type,
    fbAppId: finalFbAppId,
    faviconUrl,
  });

  const structuredData = generateStructuredData({
    title,
    description,
    tourImage,
    website,
    slug,
    type,
  });

  return (
    <>
      <Head>
        {/* Basic Meta Tags */}
        <title>{metaTags.title}</title>
        <meta name="description" content={metaTags.description} />
        <meta charSet={charset} />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no"
        />

        {/* Open Graph Meta Tags */}
        {Object.entries(metaTags.ogTags).map(([property, content]) => (
          <meta key={property} property={property} content={content} />
        ))}

        {/* Twitter Card Meta Tags */}
        {Object.entries(metaTags.twitterTags).map(([name, content]) => (
          <meta key={name} name={name} content={content} />
        ))}

        {/* Facebook Meta Tags */}
        {Object.entries(metaTags.facebookTags).map(([property, content]) => (
          <meta key={property} property={property} content={content} />
        ))}

        {/* Structured Data */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(structuredData),
          }}
        />

        {/* Favicon */}
        {faviconUrl && <link rel="icon" sizes="16x16" href={`${faviconUrl}`} />}

        {/* Facebook Pixel Noscript */}
        <noscript>
          <img
            height="1"
            width="1"
            alt=""
            style={{ display: 'none' }}
            src="https://www.facebook.com/tr?id=904257603695477&ev=PageView&noscript=1"
          />
        </noscript>
      </Head>

      <Script
        id="fb-connect"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
        !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '904257603695477');
              ${fbPixel && fbPixel.length > 0 ? `fbq('init', ${fbPixel});` : ''}
              fbq('track', 'PageView');
        `,
        }}
      />

      <script
        dangerouslySetInnerHTML={{
          __html: `window.fbAsyncInit = function() {
          FB.init({
            appId            : '${finalFbAppId}',
            autoLogAppEvents : true,
            xfbml            : true,
            version          : 'v15.0'
          });
        };`,
        }}
      />

      {htmlCode && (
        <InnerHTML html={htmlCode} />
      )}

      {children}
    </>
  );
};

export default PageHeader;
